import type { SubmitEvent } from "react";

import { useTranslation } from "react-i18next";

import { useCreateEventDirections, useUpdateEventDirections } from "@/api/bale";
import { isValidationErrorDetail } from "@/compat";
import { Card, CardActionsHeader, CardTextContent } from "@/components/Card";
import { DragHandle } from "@/components/drag-columns/components/DragItem";
import { ErrorDetail } from "@/components/ErrorDetail";
import { Form } from "@/components/Form";
import { FormField } from "@/components/FormField";
import { IconButton } from "@/components/IconButton";
import { LocationLookupInput } from "@/components/LocationLookupInput";
import { RichTextarea } from "@/components/RichTextarea";
import { uuidVersion } from "@/fp/uuidVersion";
import { useCurrentEventSuspense } from "@/pages/EventPage/hooks/useCurrentEvent";
import { useEventWidgets } from "@/pages/EventPage/hooks/useEventWidgets";

import type { DirectionsWidget } from "./DirectionsCard";

type Props = {
  widget: DirectionsWidget;
  onSave: () => void;
  onCancel?: () => void;
};

export function EditingDirectionsCard({ widget, onSave, onCancel }: Props) {
  const { t } = useTranslation();
  const { replaceWidget, removeWidget } = useEventWidgets();
  const { data: eventId } = useCurrentEventSuspense({ query: { select: (data) => data.data.event.id } });

  const updateDirections = useUpdateEventDirections({
    mutation: {
      onSuccess: ({ data: { event_directions } }) => {
        replaceWidget(widget.id, { ...widget, ...event_directions });
        onSave();
      },
    },
  });

  const createDirections = useCreateEventDirections({
    mutation: {
      onSuccess: ({ data: { event_directions } }) => {
        replaceWidget(widget.id, event_directions);
        onSave();
      },
    },
  });

  function handleSubmit(submitEvent: SubmitEvent<HTMLFormElement>) {
    const formData = new FormData(submitEvent.currentTarget);
    const directions = formData.get("directions")! as string;
    const place_id = formData.get("place_id")! as string;
    if (uuidVersion(widget.id) === 7) {
      updateDirections.mutate({ widgetId: widget.id, data: { directions, place_id } });
    } else {
      createDirections.mutate({ eventId, data: { directions, place_id } });
    }
  }

  return (
    <Card>
      <Form error={updateDirections.error ?? createDirections.error} onSubmit={handleSubmit}>
        <DragHandle>
          <CardActionsHeader title={t("event-directions")}>
            <IconButton
              type="button"
              name="trash"
              className="text-danger"
              disabled={updateDirections.isPending || createDirections.isPending}
              onClick={() => removeWidget(widget.id)}
            />
            {!!onCancel && (
              <IconButton
                type="button"
                name="x"
                aria-label={t("cancel")}
                disabled={updateDirections.isPending || createDirections.isPending}
                onClick={onCancel}
              />
            )}
            <IconButton
              type="submit"
              name="save"
              aria-label={t("save")}
              disabled={updateDirections.isPending || createDirections.isPending}
            />
          </CardActionsHeader>
        </DragHandle>
        <CardTextContent className="flex flex-col gap-4">
          {!!updateDirections.error?.info && !isValidationErrorDetail(updateDirections.error.info) && (
            <ErrorDetail error={updateDirections.error.info} />
          )}
          {!!createDirections.error?.info && !isValidationErrorDetail(createDirections.error.info) && (
            <ErrorDetail error={createDirections.error.info} />
          )}
          <FormField title={t("event-directions-description")}>
            <RichTextarea
              name="directions"
              defaultValue={widget.directions?.raw ?? ""}
              placeholder={t("event-directions-placeholder")}
              disabled={updateDirections.isPending || createDirections.isPending}
            />
          </FormField>
          <LocationLookupInput
            name="place_id"
            disabled={updateDirections.isPending || createDirections.isPending}
            defaultValue={widget.place_id ?? ""}
            title={t("event-where")}
            placeholder={t("event-location-not-set")}
            required
          />
        </CardTextContent>
      </Form>
    </Card>
  );
}
